import { createTheme } from '@mui/material/styles';

// KashLegal brand colors
const brandColors = {
  primary: {
    main: '#1e3a8a', // Deep blue for legal professionalism
    light: '#3b82f6',
    dark: '#1e40af',
    gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
  },
  secondary: {
    main: '#dc2626', // Red for urgent/important elements
    light: '#ef4444',
    dark: '#b91c1c',
    gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
  },
  accent: {
    main: '#059669', // Green for success/positive actions
    light: '#10b981',
    dark: '#047857',
    gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
  },
  gold: {
    main: '#d97706', // Gold for premium features
    light: '#f59e0b',
    dark: '#b45309',
    gradient: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)',
  },
};

// Glassmorphism effect generator
const createGlassmorphism = (darkMode, opacity = 0.1) => ({
  background: darkMode 
    ? `rgba(255, 255, 255, ${opacity})` 
    : `rgba(255, 255, 255, ${opacity + 0.1})`,
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: darkMode 
    ? '1px solid rgba(255, 255, 255, 0.2)' 
    : '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '16px',
  boxShadow: darkMode
    ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
    : '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
});

// Mobile-first responsive breakpoints
const responsiveBreakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
  // Custom mobile breakpoints for granular control
  mobile: {
    xs: 320,
    sm: 480,
    md: 600,
    lg: 768,
    xl: 1024,
  },
};

// Create custom theme with support for light and dark modes
export const createCustomTheme = (darkMode = false) => {
  return createTheme({
    breakpoints: responsiveBreakpoints,
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: brandColors.primary.main,
        light: brandColors.primary.light,
        dark: brandColors.primary.dark,
        contrastText: '#ffffff',
      },
      secondary: {
        main: brandColors.secondary.main,
        light: brandColors.secondary.light,
        dark: brandColors.secondary.dark,
        contrastText: '#ffffff',
      },
      success: {
        main: brandColors.accent.main,
        light: brandColors.accent.light,
        dark: brandColors.accent.dark,
      },
      warning: {
        main: brandColors.gold.main,
        light: brandColors.gold.light,
        dark: brandColors.gold.dark,
      },
      error: {
        main: brandColors.secondary.main,
        light: brandColors.secondary.light,
        dark: brandColors.secondary.dark,
      },
      background: {
        default: darkMode ? '#0f172a' : '#f8fafc',
        paper: darkMode ? '#1e293b' : '#ffffff',
        glass: darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.8)',
        gradient: darkMode 
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
      },
      text: {
        primary: darkMode ? '#f1f5f9' : '#1e293b',
        secondary: darkMode ? '#94a3b8' : '#64748b',
        disabled: darkMode ? '#475569' : '#94a3b8',
      },
      divider: darkMode ? 'rgba(241, 245, 249, 0.12)' : 'rgba(30, 41, 59, 0.12)',
      // Custom palette extensions
      brand: brandColors,
      glass: createGlassmorphism(darkMode),
    },
    typography: {
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: {
        fontSize: '3rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        // Mobile responsive font sizes
        '@media (max-width: 600px)': {
          fontSize: '2rem',
          lineHeight: 1.3,
        },
        '@media (max-width: 480px)': {
          fontSize: '1.75rem',
          lineHeight: 1.4,
        },
      },
      h2: {
        fontSize: '2.25rem',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
        '@media (max-width: 600px)': {
          fontSize: '1.75rem',
        },
        '@media (max-width: 480px)': {
          fontSize: '1.5rem',
        },
      },
      h3: {
        fontSize: '1.875rem',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '-0.01em',
        '@media (max-width: 600px)': {
          fontSize: '1.5rem',
        },
        '@media (max-width: 480px)': {
          fontSize: '1.25rem',
        },
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '-0.005em',
        '@media (max-width: 600px)': {
          fontSize: '1.25rem',
        },
        '@media (max-width: 480px)': {
          fontSize: '1.125rem',
        },
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
        fontWeight: 400,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
        fontWeight: 400,
      },
      subtitle1: {
        fontSize: '1.125rem',
        fontWeight: 500,
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: 1.4,
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.4,
        letterSpacing: '0.4px',
      },
      overline: {
        fontSize: '0.75rem',
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '1px',
        textTransform: 'uppercase',
      },
    },
    shape: {
      borderRadius: 12,
    },
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: darkMode ? '#1e293b' : '#f1f5f9',
            },
            '&::-webkit-scrollbar-thumb': {
              background: darkMode ? '#475569' : '#cbd5e1',
              borderRadius: '4px',
              '&:hover': {
                background: darkMode ? '#64748b' : '#94a3b8',
              },
            },
          },
          '*': {
            '&:focus-visible': {
              outline: `2px solid ${brandColors.primary.main}`,
              outlineOffset: '2px',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '12px',
            fontWeight: 600,
            padding: '10px 20px',
            minHeight: '44px', // Touch-friendly minimum height
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            // Mobile-optimized touch targets
            '@media (max-width: 600px)': {
              padding: '12px 16px',
              minHeight: '48px',
              fontSize: '1rem',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              transition: 'left 0.5s',
            },
            '&:hover::before': {
              left: '100%',
            },
          },
          contained: {
            background: brandColors.primary.gradient,
            boxShadow: '0 4px 15px 0 rgba(30, 58, 138, 0.3)',
            '&:hover': {
              boxShadow: '0 8px 25px 0 rgba(30, 58, 138, 0.4)',
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
          outlined: {
            borderWidth: '2px',
            '&:hover': {
              borderWidth: '2px',
              background: 'rgba(30, 58, 138, 0.04)',
            },
          },
          text: {
            '&:hover': {
              background: 'rgba(30, 58, 138, 0.04)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '20px',
            background: darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: darkMode 
              ? '1px solid rgba(241, 245, 249, 0.1)' 
              : '1px solid rgba(30, 41, 59, 0.1)',
            boxShadow: darkMode
              ? '0 8px 32px 0 rgba(0, 0, 0, 0.3)'
              : '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: darkMode
                ? '0 16px 64px 0 rgba(0, 0, 0, 0.4)'
                : '0 16px 64px 0 rgba(31, 38, 135, 0.25)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: '16px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
          elevation1: {
            boxShadow: darkMode
              ? '0 4px 16px 0 rgba(0, 0, 0, 0.2)'
              : '0 4px 16px 0 rgba(31, 38, 135, 0.1)',
          },
          elevation4: {
            boxShadow: darkMode
              ? '0 8px 32px 0 rgba(0, 0, 0, 0.3)'
              : '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: '0 20px 20px 0',
            background: darkMode 
              ? 'rgba(15, 23, 42, 0.95)'
              : 'rgba(248, 250, 252, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRight: darkMode 
              ? '1px solid rgba(241, 245, 249, 0.1)' 
              : '1px solid rgba(30, 41, 59, 0.1)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: darkMode 
              ? 'rgba(15, 23, 42, 0.8)'
              : 'rgba(248, 250, 252, 0.8)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: darkMode
              ? '0 4px 16px 0 rgba(0, 0, 0, 0.2)'
              : '0 4px 16px 0 rgba(31, 38, 135, 0.1)',
            borderBottom: darkMode 
              ? '1px solid rgba(241, 245, 249, 0.1)' 
              : '1px solid rgba(30, 41, 59, 0.1)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: brandColors.primary.light,
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                  borderColor: brandColors.primary.main,
                },
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: brandColors.primary.main,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            fontWeight: 500,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
          filled: {
            background: brandColors.primary.gradient,
            color: '#ffffff',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: 'rgba(30, 58, 138, 0.08)',
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            overflow: 'hidden',
          },
          bar: {
            background: brandColors.primary.gradient,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            background: darkMode 
              ? 'rgba(241, 245, 249, 0.1)'
              : 'rgba(30, 41, 59, 0.1)',
          },
        },
      },
    },
  });
};

// Additional utility functions for custom styling
export const createGradientText = (gradient) => ({
  background: gradient,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

export const createGlowEffect = (color, intensity = 0.3) => ({
  boxShadow: `0 0 20px ${color}${Math.round(intensity * 255).toString(16)}`,
});

export const createPulseAnimation = () => ({
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.05)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
  animation: 'pulse 2s infinite',
});

export const createFloatAnimation = () => ({
  '@keyframes float': {
    '0%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-10px)',
    },
    '100%': {
      transform: 'translateY(0px)',
    },
  },
  animation: 'float 3s ease-in-out infinite',
});

export const createShimmerAnimation = () => ({
  '@keyframes shimmer': {
    '0%': {
      backgroundPosition: '-468px 0',
    },
    '100%': {
      backgroundPosition: '468px 0',
    },
  },
  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
  backgroundSize: '1000px 100%',
  animation: 'shimmer 2s infinite linear',
});

// Mobile-specific utility functions
export const createMobileStyles = (theme) => ({
  // Safe area styling for devices with notches
  safeArea: {
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
  },
  
  // Touch-friendly spacing
  touchSpacing: {
    minHeight: '44px',
    minWidth: '44px',
    padding: theme.spacing(1.5),
    margin: theme.spacing(0.5),
  },
  
  // Mobile container styles
  mobileContainer: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  
  // Responsive grid for mobile
  responsiveGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    },
  },
  
  // Mobile card styles
  mobileCard: {
    borderRadius: '16px',
    margin: theme.spacing(1),
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0.5),
      padding: theme.spacing(1.5),
    },
  },
  
  // Swipeable container
  swipeableContainer: {
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    scrollSnapType: 'x mandatory',
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
  
  // Pull-to-refresh indicator
  pullToRefresh: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60px',
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    borderRadius: '0 0 20px 20px',
    transform: 'translateY(-100%)',
    transition: 'transform 0.3s ease',
    '&.active': {
      transform: 'translateY(0)',
    },
  },
  
  // Bottom navigation safe area
  bottomNavSafeArea: {
    paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
  },
  
  // Floating action button positioning
  fab: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: theme.zIndex.fab,
    [theme.breakpoints.down('sm')]: {
      bottom: 'calc(env(safe-area-inset-bottom) + 80px)', // Above bottom nav
      right: theme.spacing(2),
    },
  },
});

// Responsive spacing utility
export const responsiveSpacing = (theme, multipliers) => ({
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(multipliers.mobile || 1),
    margin: theme.spacing(multipliers.mobile || 1),
  },
  [theme.breakpoints.between('sm', 'md')]: {
    padding: theme.spacing(multipliers.tablet || 2),
    margin: theme.spacing(multipliers.tablet || 2),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(multipliers.desktop || 3),
    margin: theme.spacing(multipliers.desktop || 3),
  },
});

// Create responsive typography scale
export const responsiveTypography = (baseFontSize, scaleRatio = 1.2) => ({
  '@media (max-width: 480px)': {
    fontSize: `${baseFontSize * 0.875}rem`,
  },
  '@media (min-width: 481px) and (max-width: 768px)': {
    fontSize: `${baseFontSize}rem`,
  },
  '@media (min-width: 769px)': {
    fontSize: `${baseFontSize * scaleRatio}rem`,
  },
});

// Default theme (light mode)
export const theme = createCustomTheme(false);

// Export brand colors for use in components
export { brandColors };